import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Download } from 'lucide-react';

interface SessionData {
  id: string;
  userId: string;
  startTime: string;
  endTime?: string;
  duration: string;
  featuresUsed: string[];
  status: 'active' | 'completed' | 'error';
}

interface AuthEvent {
  id: string;
  userId: string;
  event: 'login' | 'logout' | 'failed_login';
  timestamp: string;
  ipAddress: string;
  userAgent: string;
}

const ActivityPage: React.FC = () => {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [authEvents, setAuthEvents] = useState<AuthEvent[]>([]);
  const [dateFilter, setDateFilter] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'sessions' | 'auth'>('sessions');

  useEffect(() => {
    const fetchActivityData = async () => {
      setLoading(true);
      try {
        const [sessionsResponse, authResponse] = await Promise.all([
          fetch(`/api/admin/sessions?from=${dateFilter.from}&to=${dateFilter.to}`),
          fetch(`/api/admin/auth-events?from=${dateFilter.from}&to=${dateFilter.to}`)
        ]);

        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          setSessions(sessionsData);
        } else {
          setSessions([
            {
              id: 'sess_001',
              userId: 'user_123',
              startTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              endTime: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
              duration: '1h 30m',
              featuresUsed: ['workspace', 'buildstack', 'knowledge'],
              status: 'completed'
            },
            {
              id: 'sess_002',
              userId: 'user_456',
              startTime: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
              duration: '45m',
              featuresUsed: ['workspace', 'settings'],
              status: 'active'
            },
            {
              id: 'sess_003',
              userId: 'user_789',
              startTime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
              endTime: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
              duration: '1h 15m',
              featuresUsed: ['marketplace', 'buildstack'],
              status: 'error'
            }
          ]);
        }

        if (authResponse.ok) {
          const authData = await authResponse.json();
          setAuthEvents(authData);
        } else {
          setAuthEvents([
            {
              id: 'auth_001',
              userId: 'user_123',
              event: 'login',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              ipAddress: '192.168.1.100',
              userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
            },
            {
              id: 'auth_002',
              userId: 'user_456',
              event: 'login',
              timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
              ipAddress: '192.168.1.101',
              userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36'
            },
            {
              id: 'auth_003',
              userId: 'unknown',
              event: 'failed_login',
              timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
              ipAddress: '10.0.0.50',
              userAgent: 'curl/7.68.0'
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch activity data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityData();
  }, [dateFilter]);

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`/api/admin/sessions/export?from=${dateFilter.from}&to=${dateFilter.to}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `beehive-sessions-${dateFilter.from}-${dateFilter.to}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const csvContent = [
          'Session ID,User ID,Start Time,End Time,Duration,Features Used,Status',
          ...sessions.map(session => 
            `${session.id},${session.userId},${session.startTime},${session.endTime || 'N/A'},${session.duration},"${session.featuresUsed.join(', ')}",${session.status}`
          )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `beehive-sessions-${dateFilter.from}-${dateFilter.to}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const getStatusColor = (status: SessionData['status']) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-500/20';
      case 'completed': return 'text-blue-400 bg-blue-500/20';
      case 'error': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getAuthEventColor = (event: AuthEvent['event']) => {
    switch (event) {
      case 'login': return 'text-green-400 bg-green-500/20';
      case 'logout': return 'text-blue-400 bg-blue-500/20';
      case 'failed_login': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Users className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white text-drop-shadow">User Activity Dashboard</h1>
            <p className="text-slate-400">Monitor user sessions and authentication events</p>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex gap-2">
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
              className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 px-3 py-2 text-white text-sm"
            />
            <input
              type="date"
              value={dateFilter.to}
              onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
              className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 px-3 py-2 text-white text-sm"
            />
          </div>
          
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex gap-4 border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('sessions')}
          className={`pb-3 px-1 border-b-2 transition-colors ${
            activeTab === 'sessions'
              ? 'border-blue-400 text-blue-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          User Sessions
        </button>
        <button
          onClick={() => setActiveTab('auth')}
          className={`pb-3 px-1 border-b-2 transition-colors ${
            activeTab === 'auth'
              ? 'border-blue-400 text-blue-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          Auth Events
        </button>
      </div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1"
      >
        {activeTab === 'sessions' ? (
          <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50 border-b border-slate-700/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Session ID</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">User ID</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Start Time</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Duration</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Features Used</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, index) => (
                    <motion.tr
                      key={session.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-slate-700/30 hover:bg-slate-800/30"
                    >
                      <td className="p-4 font-mono text-sm text-slate-300">{session.id}</td>
                      <td className="p-4 text-slate-300">{session.userId}</td>
                      <td className="p-4 text-slate-300 text-sm">
                        {new Date(session.startTime).toLocaleString()}
                      </td>
                      <td className="p-4 text-slate-300 text-sm">{session.duration}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {session.featuresUsed.map((feature) => (
                            <span
                              key={feature}
                              className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                          {session.status}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50 border-b border-slate-700/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">User ID</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Event</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Timestamp</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">IP Address</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {authEvents.map((event, index) => (
                    <motion.tr
                      key={event.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-slate-700/30 hover:bg-slate-800/30"
                    >
                      <td className="p-4 text-slate-300">{event.userId}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAuthEventColor(event.event)}`}>
                          {event.event.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {new Date(event.timestamp).toLocaleString()}
                      </td>
                      <td className="p-4 font-mono text-sm text-slate-300">{event.ipAddress}</td>
                      <td className="p-4 text-slate-300 text-sm max-w-xs truncate" title={event.userAgent}>
                        {event.userAgent}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ActivityPage;
